import React, { ComponentType, forwardRef, HTMLProps, MouseEventHandler, useCallback } from 'react'
import { scheduleIdleCallback } from '../../utils/browser-utils'

interface ExternalLinkProps extends Omit<HTMLProps<HTMLAnchorElement>, 'as'> {
    as?: ComponentType<any> | keyof React.JSX.IntrinsicElements
    noCrawl?: boolean
    onClick?: MouseEventHandler
    /* Props to be passed to the component that is passed in the `as` prop. */
    [others: string]: any
}

function _goToUrl(url: string, target?: string) {
    // Use scheduleIdleCallback to yield to the main thread before navigation.
    // This allows the browser to paint the click visual response (e.g., button active state)
    // before the heavy navigation/unload process begins, improving INP scores.
    scheduleIdleCallback(() => {
        if (target && target !== '_self') {
            try {
                const win = window.open(url, target)
                if (win) {
                    win.focus()
                } else {
                    // Reaching here means the pop-up was blocked.
                    // TODO: Show an alert to user to allow pop-ups.
                }
            } catch (error) {
                console.error({ error })
            }
        } else {
            globalThis.location.href = url
        }
    })
}

const ExternalLink = forwardRef<HTMLAnchorElement, ExternalLinkProps>((props, ref) => {
    const { as, noCrawl, onClick, href, target, autoRedirect, ...rest } = props

    // TODO: Style this anchor. Give it good default styles according to application's theme.
    const Component = as ?? 'a'

    const _autoRedirect = autoRedirect ?? true

    const _handleClick: MouseEventHandler = useCallback(
        event => {
            // Prevent default immediately to avoid double navigation
            if (_autoRedirect && noCrawl && href) {
                event.preventDefault()
            }

            // Schedule onClick callback to yield to browser for visual feedback
            if (onClick) {
                onClick(event)
            }

            // Navigate after yielding to main thread
            if (_autoRedirect && noCrawl && href) {
                _goToUrl(href, target)
            }
        },
        [_autoRedirect, href, noCrawl, onClick, target],
    )

    if (noCrawl) {
        return <Component ref={ref} onClick={_handleClick} target={target} {...rest} />
    }
    return <Component ref={ref} onClick={_handleClick} href={href} target={target} {...rest} />
})

ExternalLink.displayName = 'ExternalLink'

export default ExternalLink
